import { isActivationLanding } from '@/Domain/utils/accountActivation';

// Survives the Google OAuth round-trip via sessionStorage; email login reads the URL directly.
export const OAUTH_NEXT_STORAGE_KEY = 'qc_oauth_next';
// Mirrors ErrorCodes.PASSKEY_REQUIRED; errorMapper imports i18n, which imports this module via the URL preflight.
const PASSKEY_REQUIRED_LOGIN_ERROR = 'PASSKEY_REQUIRED';

// Same-origin relative path only: resolve against our origin so URL-parser tricks a naive string check misses (backslash, embedded tab/newline/CR that normalize to `//host`) can't smuggle a cross-origin redirect into window.location.
export function getSafeNextPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/')) {
    return null;
  }
  try {
    const url = new URL(raw, window.location.origin);
    if (url.origin !== window.location.origin) {
      return null;
    }
    const path = url.pathname + url.search + url.hash;
    // Dot-segments can collapse to a leading `//` (e.g. `/..//evil.com` -> `//evil.com`),
    // which is protocol-relative and navigates cross-origin — reject it.
    if (path.startsWith('//')) {
      return null;
    }
    return path;
  } catch {
    return null;
  }
}

export function readNextParamFromSearch(search: string): string | null {
  return getSafeNextPath(new URLSearchParams(search).get('next'));
}

// Mirrors REDACTED_PATH_PREFIXES in appquickchat/logging_utils/credential_redaction.py: a `next`
// under one of these carries a single-use secret as its next path segment (e.g. the channel-link
// confirmation bounce lands on `/?next=%2Flink-channel%2F<secret>`). posthog-js autocaptures the
// page URL on every event, so a `next` matching one of these must never accompany an analytics capture.
const CREDENTIAL_BEARING_NEXT_PREFIXES = [
  '/link-channel/',
  '/authorize-marketing-send/',
  '/account/confirm-email-change/',
];

export function isCredentialBearingNext(next: string | null): boolean {
  return !!next && CREDENTIAL_BEARING_NEXT_PREFIXES.some((prefix) => next.startsWith(prefix));
}

export type ActivationResume = {
  /** Same-origin path to resume after activation; null when absent or rejected. */
  resume: string | null;
  /** The activation URL carried a `next` at all. `present && !resume` = getSafeNextPath rejected it. */
  present: boolean;
};

// Read + strip the activate view's `?account_activated=1&next=` resume path so its OAuth params
// never enter telemetry. Gated on `account_activated` to leave the logged-out landing's `next`
// (which useLogin / entryQueryClient still read from the URL) untouched.
export function consumeActivationResumeNext(): ActivationResume {
  if (!isActivationLanding()) {
    return { resume: null, present: false };
  }
  const params = new URLSearchParams(window.location.search);
  const present = params.has('next');
  const resume = getSafeNextPath(params.get('next'));
  if (present) {
    const url = new URL(window.location.href);
    url.searchParams.delete('next');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }
  return { resume, present };
}

// A Google sign-in refused for a passkey-only account lands on `/?login_error=PASSKEY_REQUIRED` without the stored `next`; put it back so the passkey sign-in resumes it.
export function restoreOAuthNextAfterPasskeyRequired(): void {
  const params = new URLSearchParams(window.location.search);
  if (params.get('login_error') !== PASSKEY_REQUIRED_LOGIN_ERROR || params.has('next')) {
    return;
  }
  const stored = getSafeNextPath(sessionStorage.getItem(OAUTH_NEXT_STORAGE_KEY));
  sessionStorage.removeItem(OAUTH_NEXT_STORAGE_KEY);
  if (!stored) {
    return;
  }
  const url = new URL(window.location.href);
  url.searchParams.set('next', stored);
  window.history.replaceState({}, '', url.pathname + url.search + url.hash);
}
